import React from 'react';
import styled from 'styled-components';
import Text from '../components/Text/Text.js';
import Box from '../components/Box/Box.js';
import FuegokitLink from '../components/Link/Link.js';
import { get } from '../constants.js';

/**
 * The SectionHeader displays a title for each section of the Getting Started page. A `basic` SectionHeader will only display a title as an h2 tag.
 * An `extended` SectionHeader will display a "question mark" icon and additional instructions, if
 * an instructions prop of type SectionHeaderAction is passed in to the component.
 * 
 * Typically, you'd use the `extended` variant with the Core Features card section of the Getting Started page, and pass an instructions object that has the following shape:
 * 
 ```typescript
 type SectionHeaderAction = {
    message?: string
    url?: string
    label?: string
  }
```
 * 
 */
const SectionHeader = ({
  title,
  instructions,
  as = 'div',
  variant,
  sx: sxProp
}) => {
  console.warn('SectionHeader is deprecated. Use the v1.1 layout for Getting Started screens instead. See: https://www.fuegokit.design/react-storybook/?path=/story/patterns-getting-started-page--getting-started-page-growth');
  return /*#__PURE__*/React.createElement(StyledBox, {
    as: as,
    sx: sxProp
  }, /*#__PURE__*/React.createElement(Text, {
    as: "h2",
    fontSize: 1,
    sx: {
      fontWeight: '700',
      letterSpacing: '0px',
      lineHeight: '16px',
      textTransform: 'uppercase',
      color: 'text.subtlest'
    }
  }, title), variant === 'extended' && instructions && /*#__PURE__*/React.createElement(Instructions, null, /*#__PURE__*/React.createElement(Box, {
    sx: {
      color: 'text.subtle'
    }
  }, /*#__PURE__*/React.createElement(SVG, {
    width: "16",
    height: "16",
    viewBox: "2 2 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    preserveAspectRatio: "xMidYMin slice"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM14.8284 7.15997C15.3505 7.67945 15.7181 8.3338 15.89 9.04999C16.1223 10.008 15.9918 11.0181 15.5235 11.8856C15.0552 12.7531 14.2824 13.4165 13.354 13.748C13.149 13.821 13.004 14.003 13.004 14.22V15.002C13.004 15.2677 12.8984 15.5226 12.7105 15.7105C12.5226 15.8984 12.2677 16.004 12.002 16.004C11.7363 16.004 11.4814 15.8984 11.2935 15.7105C11.1056 15.5226 11 15.2677 11 15.002V12.988C11.0034 12.725 11.1103 12.474 11.2975 12.2893C11.4846 12.1046 11.7369 12.001 11.9998 12.001H11.993C13.098 12.001 13.996 11.103 13.996 10.001C13.984 9.47739 13.7676 8.97927 13.3929 8.61327C13.0183 8.24727 12.5153 8.04248 11.9915 8.04275C11.4678 8.04301 10.9649 8.2483 10.5907 8.61467C10.2164 8.98104 10.0004 9.47937 9.989 10.003C9.987 10.554 9.539 11.001 8.987 11.001C8.74019 10.9995 8.50281 10.906 8.32123 10.7388C8.13964 10.5717 8.02689 10.3428 8.005 10.097L8 9.94999C8.017 7.44599 10.321 5.51399 12.933 6.10799C13.6501 6.27628 14.3063 6.64048 14.8284 7.15997ZM13 18C13 18.5523 12.5523 19 12 19C11.4477 19 11 18.5523 11 18C11 17.4477 11.4477 17 12 17C12.5523 17 13 17.4477 13 18Z",
    fill: "currentColor",
    id: "my-svg"
  }))), /*#__PURE__*/React.createElement(Aside, {
    as: "p",
    sx: {
      color: 'text.subtle',
      fontWeight: 'normal',
      fontSize: '2'
    }
  }, instructions.message, " ", /*#__PURE__*/React.createElement(FuegokitLink, {
    href: instructions.url
  }, instructions.label), ".")));
};
SectionHeader.displayName = "SectionHeader";
const StyledBox = styled(Box).withConfig({
  displayName: "SectionHeader__StyledBox",
  componentId: "sc-pno6i0-0"
})(["display:flex;flex-direction:column;gap:8px;justify-content:space-between;align-items:flex-start;margin-bottom:14px;max-width:994px;@media (min-width:", "){align-items:center;flex-direction:row;}@media (min-width:", "){}"], get('breakpoints.0'), get('breakpoints.2'));
const Instructions = styled.span.withConfig({
  displayName: "SectionHeader__Instructions",
  componentId: "sc-pno6i0-1"
})(["display:flex;align-items:center;gap:4px;@media (min-width:", "){align-items:center;}"], get('breakpoints.1'));
const SVG = styled.svg.withConfig({
  displayName: "SectionHeader__SVG",
  componentId: "sc-pno6i0-2"
})(["display:none;@media (min-width:", "){display:block;}@media (min-width:", "){}"], get('breakpoints.0'), get('breakpoints.1'));
const Aside = styled(Text).withConfig({
  displayName: "SectionHeader__Aside",
  componentId: "sc-pno6i0-3"
})(["order:1;max-width:264px;@media (min-width:", "){order:2;max-width:revert;}"], get('breakpoints.1'));
SectionHeader.displayName = 'SectionHeader';
var SectionHeader$1 = SectionHeader;

export { SectionHeader$1 as default };
